import { supabase } from "../lib/supabase";
import { classifyAnalyticsPlatform } from "../utils/platform";

// These accounts are retained in the database but excluded from analytics by
// default. Keep this list limited to confirmed test accounts.
export const ANALYTICS_TEST_ACCOUNT_EMAILS = [
  "saf17saf@gmail.com",
  "revathims764@gmail.com",
  "msrenukareddy.3@gmail.com",
  "ms.revathi418@gmail.com",
  "avo143avo@gmail.com",
  "prakhartomar95@gmail.com",
  "sunkireddygarirevathi11@gmail.com",
  "surya.mettupalli@gmail.com",
  "surya@deepscan.security",
  "prakhar@growthguard.com",
  "prakhar@deepscan.security",
  "deepscan@growthguard.com",
  "surya66@gmail.com",
  "surya@growthguard.com",
  "surya@cyberimmune.com",
  "susmitharajala03@gmail.com",
  "safuwan.m@nobroker.in",
  "safuwanhilu@gmail.com",
  "prax.flash@gmail.com",
  "surya@skillcompliance.com",
  "deepscan-peer@deepscan.testinator.com",
  "idor-retest-a-1783897356-jo6xba@deepscan.testinator.com",
  "idor-retest-a-1783897404-ln5zml@deepscan.testinator.com",
  "idor-retest-b-1783897356-jo6xba@deepscan.testinator.com",
  "idor-retest-b-1783897404-ln5zml@deepscan.testinator.com",
  "idor-v2-a-1783897514-l3kylc@deepscan.testinator.com",
  "idor-v2-b-1783897519-mga9xf@deepscan.testinator.com",
  "jwttest_1770806964@deepscantest.com",
  "nonexist_xyz_9f3a2b7c@mailinator.com",
  "pwtest0_1770806913@deepscantest.com",
  "pwtest1_1770806915@deepscantest.com",
  "pwtest4_1770806919@deepscantest.com",
  "pwtest5_1770806921@deepscantest.com",
  "pwtest6_1770806923@deepscantest.com",
  "pwtest7_1770806925@deepscantest.com",
  "red-team-xss-1779981292416@deepscan.testinator.com",
  "retest-a2-1783900022-e9h8uu@deepscan.testinator.com",
  "retest-b2-1783900027-8joqhf@deepscan.testinator.com",
  "skillcompliance-test-1770805976860@deepscan.testinator.com",
  "sourish@deepscan.testinator.com",
  "test_agent_12345@example.com",
  "test-user-1779929635396@deepscan.testinator.com",
  "test@example.com",
  "testuser@deepscan.testinator.com",
  "testuser@example.com",
  "testuser1@deepscan.testinator.com",
  "testuser2@deepscan.testinator.com",
  "user1@deepscan.testinator.com",
];

export const ANALYTICS_TEST_ACCOUNT_EMAILS_STORAGE_KEY =
  "admin_analytics_test_account_emails";
export const ANALYTICS_SELECTED_TEST_ACCOUNT_EMAILS_STORAGE_KEY =
  "admin_analytics_selected_test_account_emails";

export function normalizeAnalyticsEmail(email) {
  return typeof email === "string" ? email.trim().toLowerCase() : "";
}

function uniqueEmails(emails) {
  return [...new Set((emails || []).map(normalizeAnalyticsEmail).filter(Boolean))];
}

export function getStoredAnalyticsTestAccountConfig() {
  const defaults = uniqueEmails(ANALYTICS_TEST_ACCOUNT_EMAILS);

  if (typeof window === "undefined" || !window.localStorage) {
    return { managedEmails: defaults, selectedEmails: defaults };
  }

  const parseEmails = (key) => {
    try {
      const value = window.localStorage.getItem(key);
      if (value === null) return null;
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? uniqueEmails(parsed) : null;
    } catch {
      return null;
    }
  };

  const storedManaged = parseEmails(ANALYTICS_TEST_ACCOUNT_EMAILS_STORAGE_KEY);
  const storedSelected = parseEmails(
    ANALYTICS_SELECTED_TEST_ACCOUNT_EMAILS_STORAGE_KEY,
  );
  const managedEmails = storedManaged ?? defaults;
  const legacyToggle = window.localStorage.getItem(
    "admin_analytics_exclude_test_accounts",
  );
  const selectedEmails =
    storedSelected ??
    (legacyToggle === "false" ? [] : managedEmails);

  return {
    managedEmails,
    selectedEmails: selectedEmails.filter((email) =>
      managedEmails.includes(email),
    ),
  };
}

const cachedTestUserIds = new Map();

export async function getAnalyticsTestUserIds(input = true) {
  let emails = input;
  if (input && typeof input === "object" && !Array.isArray(input)) {
    emails = Array.isArray(input.testAccountEmails)
      ? input.testAccountEmails
      : (input.excludeTestAccounts ?? true);
  }
  emails = Array.isArray(emails)
    ? uniqueEmails(emails)
    : emails
      ? uniqueEmails(ANALYTICS_TEST_ACCOUNT_EMAILS)
      : [];

  if (!emails.length) return [];
  const cacheKey = emails.join("|");
  if (cachedTestUserIds.has(cacheKey)) {
    return cachedTestUserIds.get(cacheKey);
  }

  const userIdsPromise = supabase
    .from("user_profiles")
    .select("id")
    .in("email", emails)
    .then(({ data, error }) => {
      if (error) throw error;
      return (data || []).map((profile) => profile.id).filter(Boolean);
    })
    .catch((error) => {
      cachedTestUserIds.delete(cacheKey);
      console.error("Unable to load analytics test-account exclusions:", error);
      return [];
    });

  cachedTestUserIds.set(cacheKey, userIdsPromise);
  return userIdsPromise;
}

export const ANALYTICS_PLATFORM_FILTERS = {
  ALL: "ALL",
  ISOGYAAN: "ISOGYAAN",
  SKILL_COMPLIANCE: "SKILL_COMPLIANCE",
  SKILL_COMPLIANCE_CA: "SKILL_COMPLIANCE_CA",
  PROCERT_COMPLIANCE: "PROCERT_COMPLIANCE",
  CUSTOM_DOMAINS: "CUSTOM_DOMAINS",
};

export async function getAnalyticsEnrolledUsers() {
  const fetchAllRows = async (table, select, applyFilters) => {
    const pageSize = 1000;
    const rows = [];
    let from = 0;

    while (true) {
      let query = supabase.from(table).select(select);
      query = applyFilters(query);
      const { data, error } = await query
        .order("id", { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      rows.push(...(data || []));
      if (!data || data.length < pageSize) return rows;
      from += pageSize;
    }
  };

  const [
    courseEnrollments,
    batchEnrollments,
    payments,
    partnerTransactions,
    transactionExclusions,
  ] = await Promise.all([
    fetchAllRows(
      "course_enrollments",
      "id, platform, user:user_profiles!course_enrollments_user_id_fkey(id, email, full_name), partner:partners(custom_domain)",
      (query) => query.neq("enrollment_status", "cancelled"),
    ),
    fetchAllRows(
      "batch_enrollments",
      "id, user:user_profiles!batch_enrollments_user_id_fkey(id, email, full_name), batch:training_batches(metadata, partner:partners(custom_domain))",
      (query) =>
        query.in("status", ["confirmed", "completed", "waitlisted"]),
    ),
    fetchAllRows(
      "payments",
      "id, user_id, amount, currency, payment_status, payment_method, platform, razorpay_order_id, created_at, paid_at, user:user_profiles!user_id(id, email, full_name), course:courses(title)",
      (query) => query,
    ),
    fetchAllRows(
      "partner_transactions",
      "id, user_id, total_amount, status, razorpay_order_id, created_at, user:user_profiles!user_id(id, email, full_name), course:courses(title), team:partners(name, slug, custom_domain, branding_settings)",
      (query) => query,
    ),
    fetchAllRows(
      "analytics_transaction_exclusions",
      "id, payment_id, partner_transaction_id",
      (query) => query,
    ),
  ]);

  const excludedPaymentIds = new Set(
    transactionExclusions.map((item) => item.payment_id).filter(Boolean),
  );
  const excludedPartnerTransactionIds = new Set(
    transactionExclusions
      .map((item) => item.partner_transaction_id)
      .filter(Boolean),
  );
  const partnerOrderIds = new Set(
    partnerTransactions
      .map((transaction) => transaction.razorpay_order_id)
      .filter(Boolean),
  );
  const usersByEmail = new Map();
  const addUser = (row, platformKey) => {
    const email = normalizeAnalyticsEmail(row?.user?.email);
    if (!email) return null;
    if (!usersByEmail.has(email)) {
      usersByEmail.set(email, {
        id: row.user.id,
        email,
        fullName: row.user.full_name || "",
        platforms: [],
        transactions: [],
      });
    }
    const user = usersByEmail.get(email);
    if (platformKey && !user.platforms.includes(platformKey)) {
      user.platforms.push(platformKey);
    }
    return user;
  };

  courseEnrollments.forEach((enrollment) => {
    addUser(
      enrollment,
      classifyAnalyticsPlatform({
        platform: enrollment.platform,
        customDomain: enrollment.partner?.custom_domain,
      }),
    );
  });
  batchEnrollments.forEach((enrollment) => {
    addUser(
      enrollment,
      classifyAnalyticsPlatform({
        platform: enrollment.batch?.metadata?.platform,
        customDomain: enrollment.batch?.partner?.custom_domain,
      }),
    );
  });

  payments.forEach((payment) => {
    if (
      payment.razorpay_order_id &&
      partnerOrderIds.has(payment.razorpay_order_id)
    ) {
      return;
    }
    const platformKey = classifyAnalyticsPlatform({
      platform: payment.platform,
    });
    const user = addUser(payment, platformKey);
    if (!user) return;
    user.transactions.push({
      id: payment.id,
      sourceType: "payment",
      amount: Number(payment.amount || 0),
      currency: payment.currency || "INR",
      status: payment.payment_status || "pending",
      date: payment.paid_at || payment.created_at,
      courseTitle: payment.course?.title || "Course purchase",
      sourceLabel:
        payment.payment_method === "admin_assignment"
          ? "Admin assignment"
          : "Direct sale",
      platformKey,
      isTest: excludedPaymentIds.has(payment.id),
    });
  });

  partnerTransactions.forEach((transaction) => {
    const platformKey = classifyAnalyticsPlatform({
      customDomain: transaction.team?.custom_domain,
      platform: ANALYTICS_PLATFORM_FILTERS.PROCERT_COMPLIANCE,
    });
    const user = addUser(transaction, platformKey);
    if (!user) return;
    user.transactions.push({
      id: transaction.id,
      sourceType: "partner_transaction",
      amount: Number(transaction.total_amount || 0),
      currency: transaction.team?.branding_settings?.currency || "USD",
      status: transaction.status || "pending",
      date: transaction.created_at,
      courseTitle: transaction.course?.title || "Partner purchase",
      sourceLabel: transaction.team?.name || "Partner store",
      platformKey,
      isTest: excludedPartnerTransactionIds.has(transaction.id),
    });
  });

  usersByEmail.forEach((user) => {
    user.transactions.sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0),
    );
  });

  return [...usersByEmail.values()].sort((a, b) =>
    a.email.localeCompare(b.email),
  );
}

export async function setAnalyticsTransactionTestStatus({
  sourceType,
  transactionId,
  isTest,
}) {
  const column =
    sourceType === "partner_transaction"
      ? "partner_transaction_id"
      : "payment_id";

  if (!isTest) {
    const { error } = await supabase
      .from("analytics_transaction_exclusions")
      .delete()
      .eq(column, transactionId);
    if (error) throw error;
    return;
  }

  const { data: existing, error: existingError } = await supabase
    .from("analytics_transaction_exclusions")
    .select("id")
    .eq(column, transactionId)
    .maybeSingle();
  if (existingError) throw existingError;
  if (existing) return;

  const { error } = await supabase
    .from("analytics_transaction_exclusions")
    .insert({ [column]: transactionId });
  if (error && error.code !== "23505") throw error;
}

export async function getAnalyticsTransactionExclusionIds() {
  const { data, error } = await supabase
    .from("analytics_transaction_exclusions")
    .select(
      "payment_id, partner_transaction_id, payment:payments(enrollment_id, razorpay_order_id), partner_transaction:partner_transactions(razorpay_order_id)",
    );
  if (error) throw error;

  const paymentIds = (data || []).map((item) => item.payment_id).filter(Boolean);
  const partnerTransactionIds = (data || [])
    .map((item) => item.partner_transaction_id)
    .filter(Boolean);
  const courseEnrollmentIds = (data || [])
    .map((item) => item.payment?.enrollment_id)
    .filter(Boolean);
  const linkedOrderIds = [
    ...(data || []).map((item) => item.payment?.razorpay_order_id),
    ...(data || []).map(
      (item) => item.partner_transaction?.razorpay_order_id,
    ),
  ].filter(Boolean);

  if (linkedOrderIds.length) {
    const [linkedPaymentsResult, linkedPartnerTransactionsResult] =
      await Promise.all([
        supabase
          .from("payments")
          .select("id, enrollment_id")
          .in("razorpay_order_id", linkedOrderIds),
        supabase
          .from("partner_transactions")
          .select("id")
          .in("razorpay_order_id", linkedOrderIds),
      ]);
    if (linkedPaymentsResult.error) throw linkedPaymentsResult.error;
    if (linkedPartnerTransactionsResult.error) {
      throw linkedPartnerTransactionsResult.error;
    }
    paymentIds.push(
      ...(linkedPaymentsResult.data || []).map((item) => item.id),
    );
    courseEnrollmentIds.push(
      ...(linkedPaymentsResult.data || [])
        .map((item) => item.enrollment_id)
        .filter(Boolean),
    );
    partnerTransactionIds.push(
      ...(linkedPartnerTransactionsResult.data || []).map((item) => item.id),
    );
  }

  const uniquePaymentIds = [...new Set(paymentIds)];
  const uniquePartnerTransactionIds = [...new Set(partnerTransactionIds)];

  const enrollmentFilters = [];
  if (uniquePaymentIds.length) {
    enrollmentFilters.push(`payment_id.in.(${uniquePaymentIds.join(",")})`);
  }
  if (uniquePartnerTransactionIds.length) {
    enrollmentFilters.push(
      `transaction_id.in.(${uniquePartnerTransactionIds.join(",")})`,
    );
  }

  if (enrollmentFilters.length) {
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from("course_enrollments")
      .select("id")
      .or(enrollmentFilters.join(","));
    if (enrollmentsError) throw enrollmentsError;
    courseEnrollmentIds.push(
      ...(enrollments || []).map((item) => item.id).filter(Boolean),
    );
  }

  return {
    paymentIds: uniquePaymentIds,
    partnerTransactionIds: uniquePartnerTransactionIds,
    courseEnrollmentIds: [...new Set(courseEnrollmentIds)],
  };
}

export function excludeAnalyticsUsers(query, userIds, column = "user_id") {
  if (!userIds?.length) return query;
  return query.not(column, "in", `(${userIds.join(",")})`);
}

export function excludeAnalyticsTransactions(query, transactionIds, column = "id") {
  if (!transactionIds?.length) return query;
  return query.not(column, "in", `(${transactionIds.join(",")})`);
}
